/**
 * WHERE SOMEBODY WAS GOING BEFORE THEY WERE ASKED TO SIGN IN.
 *
 * Every acknowledgement reminder links to `/acknowledge/{ackId}/{policyId}`.
 * A nurse who is signed out clicks it, is asked to sign in, asks for a link,
 * opens the link from her inbox, and until now landed on her task list, one
 * click away from the thing she had already clicked on. `RequireAuth` did
 * remember the destination, in React router state, which does not survive
 * leaving the application for an inbox and coming back through a different
 * URL.
 *
 * THE DESTINATION STAYS IN THE BROWSER, NEVER IN THE EMAIL. It could have
 * been put in the emailed link instead, and that would work across devices.
 * It would also mean the address of a specific person's specific compliance
 * task travelling in the query string of a message, being logged by every
 * mail relay between here and the hospital, and needing to be trusted again
 * when it came back. This is a path in `localStorage` on the machine that
 * asked for the link, so nothing about it is transmitted anywhere and there is
 * nothing new to trust.
 *
 * The cost is honest and worth stating: if the link is requested on a ward
 * workstation and opened on a phone, the phone has no stored destination and
 * lands on the task list, which is what happened before and is not wrong.
 *
 * NO OPEN REDIRECT. A stored value is a candidate, not an instruction. It is
 * re-validated on the way out against `isSafeDestination`, which requires a
 * path within this application: one leading slash, no scheme, no host, no
 * backslash, no control characters, a first segment this app actually routes,
 * and not an authentication route. A value that fails any of that is dropped
 * and the person lands home.
 */

/** Where the candidate lives. One key, per browser, per origin. */
export const RETURN_TO_KEY = 'somexai.returnTo';

/** Long enough for a sign-in link to arrive and be opened; the link itself lives 15 minutes. */
export const RETURN_TO_TTL_MS = 30 * 60 * 1000;

/** A path longer than this is not a route of ours. */
const MAX_LENGTH = 512;

/**
 * The first path segment of every route this application serves, as
 * `app/routes.tsx` declares them.
 *
 * Deliberately a LIST rather than a pattern. A pattern that allowed anything
 * beginning with a slash would be an open redirect the day somebody adds a
 * route that proxies elsewhere, and a list is the thing a reviewer can read.
 * `returnTo.test.ts` reads the route table and fails if a route root appears
 * that is not accounted for here, so it cannot quietly fall behind.
 */
export const APP_ROUTE_ROOTS: readonly string[] = [
  '', // the tasks page itself
  'search',
  'policies',
  'policy',
  'acknowledge',
  'my-approvals',
  'scope-of-service',
  'exceptions',
  'competencies',
  'forms',
  'help',
  'select-org',
  'settings',
  'profile',
  'admin',
  'not-authorized',
  'no-access',
];

/**
 * Roots that exist and are deliberately NOT returned to.
 *
 * `signin` and `sso` are the authentication flow itself: returning into it
 * after completing it is a loop. `console` is a different host with its own
 * front door, so a path to it from here would send somebody to a page their
 * session is not scoped for.
 */
/*
 * `welcome` is the customer's intake form, reached from an email by somebody
 * with no account. Its token IS the credential and a bare /welcome means
 * nothing, so returning anybody there after sign-in lands them on a dead page.
 */
export const NEVER_RETURN_TO: readonly string[] = ['signin', 'sso', 'console', 'welcome'];

/** Whether this is a path inside this application that it is sensible to return to. */
export function isSafeDestination(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  if (value.length === 0 || value.length > MAX_LENGTH) return false;

  // One leading slash and nothing that could be read as a host or a scheme.
  if (!value.startsWith('/')) return false;
  if (value.startsWith('//')) return false;
  if (value.includes('\\')) return false;
  if (value.includes(':')) return false;
  // Control characters, including the ones a browser strips before parsing.
  // eslint-disable-next-line no-control-regex
  if (/[ -]/.test(value)) return false;

  const path = value.split('?')[0]!.split('#')[0]!;
  const root = path.slice(1).split('/')[0] ?? '';
  if (NEVER_RETURN_TO.includes(root)) return false;
  return APP_ROUTE_ROOTS.includes(root);
}

interface Stored {
  path: string;
  at: number;
}

/** A browser that refuses storage is a browser that lands on the task list. */
function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Called when somebody is turned away from a page for not being signed in. */
export function rememberDestination(path: string, now = Date.now()): void {
  if (!isSafeDestination(path)) return;
  try {
    storage()?.setItem(RETURN_TO_KEY, JSON.stringify({ path, at: now } satisfies Stored));
  } catch {
    // Private windows, blocked site data, a full quota. Not worth a failure.
  }
}

/** Read the candidate without consuming it, so a failed redemption can retry. */
export function peekDestination(now = Date.now()): string | null {
  const raw = (() => {
    try {
      return storage()?.getItem(RETURN_TO_KEY) ?? null;
    } catch {
      return null;
    }
  })();
  if (!raw) return null;

  let stored: Stored;
  try {
    stored = JSON.parse(raw) as Stored;
  } catch {
    return null;
  }
  if (typeof stored?.at !== 'number' || now - stored.at > RETURN_TO_TTL_MS) return null;
  // Re-validated on the way OUT as well as on the way in: what is in storage
  // is a candidate that somebody else's tab, or an older build, may have put
  // there.
  return isSafeDestination(stored.path) ? stored.path : null;
}

export function clearDestination(): void {
  try {
    storage()?.removeItem(RETURN_TO_KEY);
  } catch {
    // Nothing to do, and nothing that depends on it.
  }
}
